import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { addRecentlyViewed, getStockByTicker } from "../../api/api";
import { getPricePercentChange } from "../../api/stocks_api";
import StockLayout from "./stockLayout";

const StockDetails = () => {
  const { ticker, tab } = useParams();
  const navigate = useNavigate();
  const [notFound, setNotFound] = useState(false);

  const [stock, setStock] = useState(null);
  const [stockPriceChange, setStockPriceChange] = useState(null);


  const activeTab =
    tab?.charAt(0).toUpperCase() + tab?.slice(1) || "Summary";

  useEffect(() => {
    if (!tab) {
      navigate(`/stocks/${ticker}/details/summary`, { replace: true });
      return;
    }

    const fetchDetails = async () => {
      const res = await getStockByTicker(ticker);

      if (res.success) {
        setStock(res.data);
        setNotFound(false);
        addRecentlyViewed(ticker);
      } else if (res.status === 404) {
        setNotFound(true);
        setStock(null);
      } else {
        setNotFound(true);
      }
    };

    const fetchPriceDetails = async () => {
      const res = await getPricePercentChange(ticker);
      if (res.success) {
        setStockPriceChange(res.data);
        console.log(res);
        // addRecentlyViewed(ticker);
      }
    };

    fetchDetails();
    fetchPriceDetails();
  }, [ticker, tab, navigate]);

  if (notFound) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-center"
        style={{ backgroundColor: "var(--color-bg-primary)" }}
      >
        <div>
          <h1
            style={{
              fontSize: "var(--font-size-2xl)",
              fontWeight: "var(--font-weight-bold)",
              color: "var(--color-text-primary)",
              marginBottom: "var(--spacing-sm)",
            }}
          >
            No data found
          </h1>

          <p
            style={{
              color: "var(--color-text-muted)",
              fontSize: "var(--font-size-sm)",
              marginBottom: "var(--spacing-md)",
            }}
          >
            We couldn’t find any data for <strong>{ticker}</strong>
          </p>

          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "8px 16px",
              backgroundColor: "var(--color-primary)",
              color: "#fff",
              borderRadius: "var(--radius-md)",
              fontWeight: "var(--font-weight-semibold)",
              border: "none",
              cursor: "pointer",
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!stock) {
    return (
      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--color-bg-primary)' }}
      >
        {/* Skeleton header */}
        <div
          className="pt-4 pb-2 flex items-center justify-between"
          style={{ padding: 'var(--spacing-md) var(--spacing-lg) var(--spacing-sm)' }}
        >
          <div
            className="h-4 w-16 rounded animate-pulse"
            style={{
              backgroundColor: 'var(--color-surface-hover)',
              borderRadius: 'var(--radius-sm)'
            }}
          />
          <div className="flex gap-2">
            <div
              className="w-10 h-10 rounded-xl animate-pulse"
              style={{
                backgroundColor: 'var(--color-surface-hover)',
                borderRadius: 'var(--radius-lg)'
              }}
            />
            <div
              className="w-10 h-10 rounded-xl animate-pulse"
              style={{
                backgroundColor: 'var(--color-surface-hover)',
                borderRadius: 'var(--radius-lg)'
              }}
            />
            <div
              className="w-24 h-10 rounded-xl animate-pulse"
              style={{
                backgroundColor: 'var(--color-surface-hover)',
                borderRadius: 'var(--radius-lg)'
              }}
            />
          </div>
        </div>

        <div
          className="pt-2 pb-5"
          style={{ padding: 'var(--spacing-sm) var(--spacing-lg) var(--spacing-lg)' }}
        >
          <div className="flex items-start gap-4">
            <div
              className="w-14 h-14 rounded-2xl animate-pulse"
              style={{
                backgroundColor: 'var(--color-surface-hover)',
                borderRadius: 'var(--radius-xl)'
              }}
            />
            <div className="flex-1">
              <div
                className="h-6 w-32 rounded-lg animate-pulse mb-2"
                style={{
                  backgroundColor: 'var(--color-surface-hover)',
                  borderRadius: 'var(--radius-md)'
                }}
              />
              <div
                className="h-4 w-48 rounded-lg animate-pulse mb-4"
                style={{
                  backgroundColor: 'var(--color-surface-hover)',
                  borderRadius: 'var(--radius-md)'
                }}
              />
              <div
                className="h-10 w-40 rounded-lg animate-pulse"
                style={{
                  backgroundColor: 'var(--color-surface-hover)',
                  borderRadius: 'var(--radius-md)'
                }}
              />
            </div>
          </div>
        </div>

        {/* Skeleton tabs */}
        <div style={{ padding: '0 var(--spacing-lg)' }}>
          <div
            className="flex gap-1 rounded-2xl p-1.5"
            style={{
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border-primary)',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--spacing-xs)'
            }}
          >
            {[...Array(7)].map((_, i) => (
              <div
                key={i}
                className="h-9 w-20 rounded-xl animate-pulse"
                style={{
                  backgroundColor: 'var(--color-surface-hover)',
                  borderRadius: 'var(--radius-lg)'
                }}
              />
            ))}
          </div>
        </div>

        {/* Skeleton content */}
        <div
          className="mt-6 flex gap-3"
          style={{
            padding: '0 var(--spacing-lg)',
            marginTop: 'var(--spacing-lg)'
          }}
        >
          <div className="flex-1 space-y-4">
            <div
              className="h-[500px] rounded-2xl animate-pulse"
              style={{
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border-primary)',
                borderRadius: 'var(--radius-xl)',
                opacity: 0.5
              }}
            />
            <div
              className="h-64 rounded-2xl animate-pulse"
              style={{
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border-primary)',
                borderRadius: 'var(--radius-xl)',
                opacity: 0.5
              }}
            />
          </div>
          <div
            className="hidden lg:block w-[340px] h-96 rounded-2xl animate-pulse"
            style={{
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border-primary)',
              borderRadius: 'var(--radius-xl)',
              opacity: 0.5
            }}
          />
        </div>
      </div>
    );
  }

  return <StockLayout stock={stock} stockPriceChange={stockPriceChange} activeTab={activeTab} />;
};

export default StockDetails;